import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useLayoutEffect,
} from 'react'
import { Okpd2 } from '../../../../../Org/Handbook/types'
import { OrganizationInfo } from '../../../../../Org/Home/types'

import { Contract, Country } from '../../../../types'
import { CurrencyList } from '../../../types'

import HoverTooltip from '../../../../../../../components/HoverTooltip'

type PerfTotalDocSum = {
    total_doc: number
    total_doc_rub: number
}

type PerfTotalPaySum = {
    total_payment: number
    total_payment_rub: number
}

type Props = {
    contractGuid: string
    perf_number: number
    contract: Contract
    theme: string
    isActive: boolean
    currencyList: CurrencyList[]
    selected: boolean
    onClick: (contract_guid: string) => void
    onRightClick: (contract_guid: string) => void
    onDoubleClick: (contract_guid: string) => void
}

const CONTRACT_STATUS = new Map([
    ['F', 'Редактирование'],
    ['P', 'Размещено'],
    ['I', 'Недействительно'],
    ['M', 'Изменение'],
])

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function formatCountryOfOriginStrict(src: Country | null | undefined): string {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return ''

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // убрать дубли, сохраняя порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique.join(', ')
}

function getCountryListOrderedStrict(
    src: Country | null | undefined
): string[] {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return []

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // dedupe (без регистра), сохранив порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique
}

function purchaseTypeLabel(code?: string | null): string {
    switch ((code ?? '').toUpperCase()) {
        case 'G':
            return 'Товар'
        case 'W':
            return 'Работа'
        case 'S':
            return 'Услуга'
        default:
            return '' // неизвестно/пусто
    }
}

function formatTotalCount(totalCount: number) {
    // Получаем число с 5 знаками после запятой (с округлением)
    let str = totalCount.toFixed(5)
    // Если есть десятичная часть, удаляем лишние нули
    if (str.indexOf('.') !== -1) {
        // Удаляем концевые нули
        str = str.replace(/0+$/, '')
        // Если после удаления нулей осталась точка, убираем её
        str = str.replace(/\.$/, '')
    }
    // Заменяем точку на запятую
    return str.replace('.', ',')
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const ContractTableRow: React.FC<Props> = (props) => {
    const STATUS_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['Черновик', '#8D79FF'],
                  ['Готово', '#049664'],
                  ['Подготовка', '#745CFF'],
                  ['Готово к отправке', '#563AF5'],
                  ['Отправлено в ЕИС', '#049664'],
                  ['Размещена', '#636366'],
                  ['Недействительно', '#636366'],
                  ['Договор на исполнении', '#049664'],
                  ['Просрочен', '#a85252'],
                  ['Исполнение договора завершено', '#515055'],
                  ['Договор расторгнут', '#515055'],
                  ['Исполнение договора прекращено', '#515055'],
                  ['Осталось меньше 10 дней', '#aa8c08'],
                  ['Осталось меньше 5 дней', '#d5701d'],
                  ['Возможно исполнен', '#aa8c08'],
              ])
            : new Map([
                  ['Черновик', '#DFDDFF'],
                  ['Готово', '#83D2B7'],
                  ['Подготовка', '#D3CFFF'],
                  ['Готово к отправке', '#B7ACFF'],
                  ['Отправлено в ЕИС', '#83D2B7'],
                  ['Размещена', '#CFD6E2'],
                  ['Недействительно', '#CFD6E2'],
                  ['Договор на исполнении', '#83d2b7'],
                  ['Просрочен', '#ed9cae'],
                  ['Исполнение договора завершено', '#cfd6e2'],
                  ['Договор расторгнут', '#cfd6e2'],
                  ['Исполнение договора прекращено', '#cfd6e2'],
                  ['Осталось меньше 10 дней', '#e0c963'],
                  ['Осталось меньше 5 дней', '#d5701d'],
                  ['Возможно исполнен', '#e0c963'],
              ])

    const STAGE_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['Редактирование', '#745CFF'],
                  ['Подготовка к размещению', '#563AF5'],
                  ['Отправка в ЕИС', '#4406BE'],
                  ['Размещенный договор', '#636366'],
                  ['Исполнение договора', '#067d54'],
                  ['Договор закрыт', '#515055'],
              ])
            : new Map([
                  ['Редактирование', '#D3CFFF'],
                  ['Подготовка к размещению', '#B7ACFF'],
                  ['Отправка в ЕИС', '#A191FF'],
                  ['Размещенный договор', '#CFD6E2'],
                  ['Исполнение договора', '#41c497'],
                  ['Договор закрыт', '#cfd6e2'],
              ])

    const CONTRACT_STATUS_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['F', '#8E8E93'],
                  ['P', '#2A282B00'],
                  ['I', '#3A3A3C'],
                  ['M', '#636366'],
              ])
            : new Map([
                  ['F', '#E4E8F0'],
                  ['P', '#F6FBFF00'],
                  ['I', '#B7BECA'],
                  ['M', '#CFD6E2'],
              ])

    const updatedDate = new Date(props.contract.publication_date)
    updatedDate.setHours(updatedDate.getHours())

    const formattedUpdateDate = updatedDate
        .toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
        .replace(/\//g, '.')
    const formattedUpdateTime = updatedDate.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })

    return (
        <div
            className={
                props.selected
                    ? 'plan-position-table-row-selected'
                    : 'plan-position-table-row'
            }
            onClick={() => {
                props.contract.contract_stage == 'Исполнение договора' ||
                props.contract.contract_stage == 'Договор закрыт'
                    ? ''
                    : props.onClick(props.contract.guid)
            }}
            onContextMenu={(e) => {
                e.preventDefault() // Отключает стандартное контекстное меню
                props.contract.contract_stage == 'Исполнение договора' ||
                props.contract.contract_stage == 'Договор закрыт'
                    ? ''
                    : props.onRightClick(props.contract.guid)
            }}
            onDoubleClick={() => props.onDoubleClick(props.contract.guid)}
        >
            <div className="flex h-[70px] w-8 items-center justify-center text-center">
                {props.contract.contract_stage == 'Исполнение договора' ||
                props.contract.contract_stage == 'Договор закрыт' ? (
                    ''
                ) : (
                    <input
                        type="checkbox"
                        checked={props.selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                            props.onClick(props.contract.guid)
                        }}
                    />
                )}
            </div>
            <div className="flex flex-row h-[70px] w-8 items-center justify-center text-center ">
                {props.perf_number}
            </div>

            <div
                className={`flex h-[70px] w-24 p-1 items-center justify-center text-center rounded`}
                style={{
                    backgroundColor: props.contract.contract_stage
                        ? STAGE_COLOR.get(props.contract.contract_stage)
                        : 'transparent',
                }}
            >
                {props.contract.contract_stage}
            </div>
            <div
                className="flex h-[70px] w-24 p-1 items-center justify-center text-center rounded"
                style={{
                    backgroundColor: props.contract.contract_status
                        ? STATUS_COLOR.get(props.contract.contract_status)
                        : 'transparent',
                }}
            >
                {props.contract.contract_status == 'Размещена'
                    ? 'Размещено'
                    : props.contract.contract_status}
            </div>

            <div className="flex flex-1 h-[70px] text-left items-center justify-center">
                {`Сведения о договоре № ${props.contract.registration_number ? props.contract.registration_number : '-'}`}
            </div>

            <div
                className="flex h-[70px] w-24 items-center justify-center text-center rounded"
                style={{
                    backgroundColor: props.contract.status
                        ? CONTRACT_STATUS_COLOR.get(props.contract.status)
                        : 'transparent',
                }}
            >
                {CONTRACT_STATUS.get(String(props.contract.status))}
            </div>

            <div className="flex flex-row h-[70px] w-20 items-center justify-center text-center">
                {props.contract.version}
            </div>

            <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                {props.contract.publication_date ? (
                    <>
                        {formattedUpdateDate} <br /> {formattedUpdateTime}{' '}
                    </>
                ) : (
                    '-'
                )}
            </div>
        </div>
    )
}

export default ContractTableRow
